import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import {
  sendManualOverrideClear,
  sendManualOverrideCommand,
  sendRevSSHCmd,
  sendShellCmd,
} from "../../scripts/api_v2/commands_client";
import * as React from "react";
import { Text } from "../general/typography";
import useBackend from "../../hooks/use_backend";
import { useMutation } from "@tanstack/react-query";
import Snackbar from "../../scripts/snackbar";

export function ShellCmdDialog({ open, setOpen, siteId }) {
  const [shellCmd, setShellCmd] = React.useState("");
  const [shellRes, setShellRes] = React.useState([]);
  const [shellCmds, setShellCmds] = React.useState([]);
  const descriptionElementRef = React.useRef(null);
  const backend = useBackend();

  const { mutate: sendShellCmdMutation, isPending: isMutating } = useMutation({
    mutationFn: async () => {
      return await sendShellCmd(backend.fetch, siteId, shellCmd);
    },
    onSuccess: (data) => {
      setShellCmds((prev) => [...prev, shellCmd]);
      if (data?.msg) {
        setShellRes((prev) => [...prev, data.msg]);
      } else {
        setShellRes((prev) => [...prev, data]);
      }
    },
    onError: (err, variables, context) => {
      Snackbar.error("Could not send shell cmd: " + err);
    },
  });

  return (
    <Dialog
      open={open}
      onClose={() => {
        setOpen(false);
        setShellRes([]);
        setShellCmds([]);
        setShellCmd("");
      }}
      aria-labelledby="shell-dialog-title"
      aria-describedby="shell-dialog-description"
      fullWidth
      maxH
      maxWidth={"md"}
      classes={{ paper: { minHeight: "80vh", maxHeight: "80vh" } }}
    >
      <DialogTitle id="shell-dialog-title">{"Shell command"}</DialogTitle>
      <DialogContent id="scroll-dialog-description" ref={descriptionElementRef} tabIndex={-1} maxH>
        <pre>
          {shellRes.map((res, idx) => (
            <React.Fragment key={idx}>
              <Text>{shellCmds[idx]}</Text>
              <Text ml={5}>{res}</Text>
            </React.Fragment>
          ))}
        </pre>
      </DialogContent>
      <DialogContent>
        <Stack direction="row" spacing={3} mt={3}>
          <TextField
            autoFocus
            id="shellcmd"
            label="Command"
            fullWidth
            value={shellCmd}
            onChange={(e) => setShellCmd(e.target.value)}
          />
          <Button
            variant="contained"
            onClick={() => {
              sendShellCmdMutation();
            }}
          >
            Send
          </Button>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ justifyContent: "center" }}>
        <Button
          onClick={() => {
            setOpen(false);
            setShellRes([]);
            setShellCmds([]);
            setShellCmd("");
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function RevSSHCmdDialog({ open, setOpen, siteId }) {
  const [port, setPort] = React.useState(44343);
  const [tag, setTag] = React.useState("bootloader");
  const [openTunnel, setOpenTunnel] = React.useState("open");
  const [response, setResponse] = React.useState("");
  const backend = useBackend();

  const { mutate: sendRevSSHCmdMutation, isPending: isMutating } = useMutation({
    mutationFn: async () => {
      return await sendRevSSHCmd(backend.fetch, siteId, port, tag, openTunnel === "open");
    },
    onSuccess: (data) => {
      setResponse(data);
    },
    onError: (err, variables, context) => {
      Snackbar.error("Could not send rev SSH cmd: " + err);
    },
  });

  return (
    <Dialog
      open={open}
      onClose={() => {
        setOpen(false);
        setResponse("");
      }}
      aria-labelledby="rev-ssh-dialog-title"
      aria-describedby="rev-ssh-dialog-description"
      fullWidth
      maxH
      maxWidth={"xs"}
      classes={{ paper: { minHeight: "80vh", maxHeight: "80vh" } }}
    >
      <DialogTitle id="shell-dialog-title">{"Reverse SSH Command"}</DialogTitle>
      <DialogContent>
        <Stack direction="column" alignItems="center" spacing={3} mt={3}>
          <TextField
            autoFocus
            id="shellcmd"
            label="Port"
            fullWidth
            value={port}
            onChange={(e) => setPort(e.target.value)}
          />
          <ToggleButtonGroup
            value={tag}
            fullWidth
            exclusive
            onChange={(v) => (v.target ? setTag(v.target.value) : null)}
            aria-label="text alignment"
          >
            <ToggleButton value={"client"} aria-label="left aligned">
              Client
            </ToggleButton>
            <ToggleButton value={"bootloader"} aria-label="centered">
              Bootloader
            </ToggleButton>
          </ToggleButtonGroup>
          <ToggleButtonGroup
            value={openTunnel}
            fullWidth
            exclusive
            onChange={(v) => (v.target ? setOpenTunnel(v.target.value) : null)}
            aria-label="text alignment"
          >
            <ToggleButton value={"open"} aria-label="left aligned">
              Open
            </ToggleButton>
            <ToggleButton value={"close"} aria-label="centered">
              Close
            </ToggleButton>
          </ToggleButtonGroup>

          <b>{response}</b>

          <Button
            variant="contained"
            fullWidth
            onClick={() => {
              sendRevSSHCmdMutation();
            }}
          >
            Send
          </Button>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
